import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import { graphql, compose } from 'react-apollo';

// Style
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './ListingManagement.css';
import cs from '../../../components/commonStyle.css';


import CustomPagination from '../../CustomPagination';
import CommonTable from '../../CommonTable/CommonTable';
import TableAction from '../../CommonTable/TableAction';
import Loader from '../../Loader';
import DocumentStatusCell from './DocumentStatusCell';
import StatusBadge from './StatusBadge';
import {
  countListingsPendingDocumentReview,
  formatStreetAddress,
  getListingTitle,
} from './listingManagementHelpers';

// Redux Action
import { removeListing } from '../../../actions/siteadmin/ListingManagement/removeListing';
import { addListToRecommended, removeListFromRecommended } from '../../../actions/siteadmin/ListingManagement/manageRecommend';
import listingsQuery from './listingsQuery.graphql';
import messages from '../../../locale/messages';

import history from '../../../core/history';
import {
  ALLOWED_RECENT_LIMITS,
  recentLimitToQueryParam,
} from '../../../routes/siteadmin/listings/listingsRecentLimit';
import {
  ALLOWED_DOCUMENT_STATUS_FILTERS,
  buildListingsQueryString,
} from '../../../routes/siteadmin/listings/listingsDocumentFilter';

import { ManagePublishStatus } from '../../../actions/Listing/ManagePublishStatus';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import { sendEmail } from '../../../core/email/sendEmail';

class ListingManagement extends React.Component {

  static propTypes = {
    getAllListings: PropTypes.array,
    title: PropTypes.string.isRequired,
    addListToRecommended: PropTypes.func.isRequired,
    removeListFromRecommended: PropTypes.func.isRequired,
  };
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      searchList: '',
      typing: false,
      typingTimeout: 0
    }
    this.paginationData = this.paginationData.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleRecentLimitChange = this.handleRecentLimitChange.bind(this);
    this.handleDocumentStatusFilterChange = this.handleDocumentStatusFilterChange.bind(this);
  }

  queryVariables() {
    const { currentPage, searchList, recentLimit, documentStatusFilter } = this.props;
    return { currentPage, searchList, recentLimit, documentStatusFilter };
  }

  pushListingsUrl(recentLimit, documentStatusFilter) {
    const qs = buildListingsQueryString({ recentLimit, documentStatusFilter });
    if (history) {
      history.push(`/siteadmin/listings${qs}`);
    }
  }

  componentDidUpdate(prevProps) {
    const { getAllListings: { refetch } } = this.props;
    const vars = this.queryVariables();
    const prevVars = {
      currentPage: prevProps.currentPage,
      searchList: prevProps.searchList,
      recentLimit: prevProps.recentLimit,
      documentStatusFilter: prevProps.documentStatusFilter,
    };
    if (
      prevVars.recentLimit !== vars.recentLimit
      || prevVars.documentStatusFilter !== vars.documentStatusFilter
      || prevVars.currentPage !== vars.currentPage
      || prevVars.searchList !== vars.searchList
    ) {
      refetch(vars);
    }
  }

  paginationData(currentPage) {
    const { getAllListings: { refetch }, setStateVariable } = this.props;
    const variables = { ...this.queryVariables(), currentPage };
    setStateVariable(variables);
    refetch(variables);
  }
  handleClick(searchList) {
    const { getAllListings: { refetch }, setStateVariable } = this.props;
    const variables = {
      ...this.queryVariables(),
      currentPage: 1,
      searchList,
    };
    setStateVariable(variables);
    refetch(variables);
  }
  handleRecentLimitChange(e) {
    const { documentStatusFilter } = this.props;
    const value = e.target.value;
    // "All" → bare /siteadmin/listings; "Newest N" → ?recentLimit=N
    if (!value) {
      this.pushListingsUrl(null, documentStatusFilter);
      return;
    }
    const recentLimit = parseInt(value, 10);
    if (recentLimitToQueryParam(recentLimit)) {
      this.pushListingsUrl(recentLimit, documentStatusFilter);
    }
  }
  handleDocumentStatusFilterChange(e) {
    const { recentLimit } = this.props;
    const documentStatusFilter = e.target.value || null;
    this.pushListingsUrl(recentLimit, documentStatusFilter);
  }
  handleSearchChange = (e) => {
    const self = this;
    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }
    self.setState({
      searchList: e.target.value,
      typing: false,
      typingTimeout: setTimeout(function () {
        self.handleClick(self.state.searchList);
      }, 450)
    });
  }

  async deleteListing(id, type) {
    const { removeListing, getAllListings: { refetch }, setStateVariable } = this.props;
    const variables = { ...this.queryVariables(), currentPage: 1 };
    await removeListing(id, type);
    await setStateVariable(variables);
    await refetch(variables);
  }

  handleAdminPublish = async (listing) => {
    try {
      await this.props.ManagePublishStatus(listing.id, 'publish');

      const { getAllListings: { refetch } } = this.props;
      const { data } = await refetch(this.queryVariables());
      const rows = data?.getAllListings?.usersData || [];
      const updated = rows.find((row) => String(row.id) === String(listing.id));

      if (!updated?.isPublished) {
        return;
      }

      const content = {
        userName: (listing.user && listing.user.profile && listing.user.profile.firstName) || '',
        carTitle: listing.title || '',
        carId: listing.id,
      };

      const email = (updated.user && updated.user.email) || (listing.user && listing.user.email);
      if (email) {
        sendEmail(email, 'carPublished', content);
      }
    } catch (error) {
      showToaster({ messageId: 'publishFailed', toasterType: 'error', requestMessage: 'Failed to publish car.' });
    }
  }

  handleAdminUnpublish = async (listing) => {
    try {
      await this.props.ManagePublishStatus(listing.id, 'unPublish');
      // Optionally: showToaster({ messageId: 'adminUnpublished', toasterType: 'success', requestMessage: 'Car unpublished successfully!' });
      const content = {
        userName: (listing.user && listing.user.profile && listing.user.profile.firstName) || '',
        carTitle: listing.title || '',
        carId: listing.id,
      };
      // Optionally notify user
    
      if (listing.user && listing.user.email) {
        sendEmail(
          listing.user.email,
          'carUnpublished',
          content
        );
      }
      
    } catch (error) {
      showToaster({ messageId: 'unpublishFailed', toasterType: 'error', requestMessage: 'Failed to unpublish car.' });
    }
  }

  thead = () => {
    const { formatMessage } = this.props.intl;
    return [
      { data: formatMessage(messages.idLabel) },
      { data: formatMessage(messages.adminTitleLabel) },
      { data: formatMessage(messages.carOwnerName) },
      { data: formatMessage(messages.address) },
      { data: formatMessage(messages.location) },
      { data: formatMessage(messages.carValue) },
      { data: formatMessage(messages.excessValue) },
      { data: formatMessage(messages.createdDate) },
      { data: formatMessage(messages.documentLabel) },
      { data: formatMessage(messages.recommendLabel) },
      { data: formatMessage(messages.publishedLabel) },
      { data: formatMessage(messages.ready) },
    ]
  };

  tbody = () => {
    const { getAllListings: { getAllListings } } = this.props;
    const { currentPage, searchList, addListToRecommended, removeListFromRecommended } = this.props;
    const { formatMessage } = this.props.intl;
    return (getAllListings?.usersData || []).map(value => {
      const streetAddress = formatStreetAddress(value);
      const listingTitle = getListingTitle(value);
      const ownerName = value?.user?.profile?.firstName || '-';
      const ownerEmail = value?.user?.email || '-';
      const city = value?.city || '-';
      const state = value?.state || '-';
      const country = value?.country || '-';
      return {
        id: value?.id,
        data: [
          { data: <span className={s.idCell}>{value.id}</span> },
          {
            data:
              <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween, s.titleCell)}>
                <a
                  href={'/cars/' + value.id}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.listingTitleLink}
                >
                  {listingTitle}
                </a>

                <TableAction
                  onClickDelete={() => this.deleteListing(value.id, "admin")}
                  showDelete={true}
                  showView={true}
                  newLink={"/cars/" + value.id}
                  showEdit
                  editNewLink={'/become-a-owner/' + value.id + '/home'}
                  item={{ published: value.isPublished, ready: value.isReady }}
                  onClickPublish={(!value.isPublished && value.isReady) ? (() => this.handleAdminPublish(value)) : undefined}
                  onClickUnpublish={(value.isPublished) ? (() => this.handleAdminUnpublish(value)) : undefined}
                />
              </div>
          },
          {
            data: (
              <div className={s.ownerCell}>
                <div className={s.documentRow}>
                  <span className={s.documentLabel}>
                    {formatMessage(messages.name)}:
                  </span>
                  <span className={s.labeledValue}>{ownerName}</span>
                </div>
                <div className={s.documentRow}>
                  <span className={s.documentLabel}>
                    {formatMessage(messages.emailLabel)}:
                  </span>
                  <span className={cx(s.labeledValue, s.emailValue)} title={ownerEmail}>
                    {ownerEmail}
                  </span>
                </div>
              </div>
            ),
          },
          {
            data: (
              <span className={s.addressCell}>
                {streetAddress}
              </span>
            ),
          },
          {
            data: (
              <div className={s.locationCell}>
                <div className={s.documentRow}>
                  <span className={s.documentLabel}>
                    {formatMessage(messages.city)}:
                  </span>
                  <span className={s.labeledValue}>{city}</span>
                </div>
                <div className={s.documentRow}>
                  <span className={s.documentLabel}>
                    {formatMessage(messages.stateLabel)}:
                  </span>
                  <span className={s.labeledValue}>{state}</span>
                </div>
                <div className={s.documentRow}>
                  <span className={s.documentLabel}>
                    {formatMessage(messages.country)}:
                  </span>
                  <span className={s.labeledValue}>{country}</span>
                </div>
              </div>
            ),
          },
          {
            data: (
              <span className={s.valueCell}>
                {value?.carRangeId?.car_value_range ? value.carRangeId.car_value_range : '-'}
              </span>
            ),
          },
          {
            data: (
              <span className={s.valueCell}>
                {value?.carRangeId?.recommended_excess ? value.carRangeId.recommended_excess : '-'}
              </span>
            ),
          },
          {
            data: (
              <span className={s.dateCell}>
                {moment(value?.createdAt).format('MM/DD/YYYY')}
              </span>
            ),
          },
          {
            data: (
              <DocumentStatusCell
                documents={value.carDocuments}
                listId={value.id}
                userId={value.userId}
              />
            ),
          },
          {
            data: value.recommend != null ? (
              <button
                type="button"
                className={cx(s.recommendAction, s.recommendActionRemove)}
                onClick={() => removeListFromRecommended(value.id, currentPage, searchList)}
              >
                <FormattedMessage {...messages.remove} />
              </button>
            ) : (
              <button
                type="button"
                className={s.recommendAction}
                onClick={() => addListToRecommended(value.id, currentPage, searchList)}
              >
                <FormattedMessage {...messages.setLabel} />
              </button>
            ),
          },
          {
            data: (
              <StatusBadge
                active={value.isPublished}
                activeLabel={formatMessage(messages.yesLabel)}
                inactiveLabel={formatMessage(messages.noLabel)}
              />
            ),
          },
          {
            data: (
              <StatusBadge
                active={value.isReady}
                activeLabel={formatMessage(messages.yesLabel)}
                inactiveLabel={formatMessage(messages.noLabel)}
              />
            ),
          },
        ]
      }
    })
  }

  render() {
    const { getAllListings: { loading, getAllListings } } = this.props;
    const { currentPage, searchList, recentLimit, documentStatusFilter } = this.props;
    const { formatMessage } = this.props.intl;
    const pageSize = recentLimit || 10;
    const recentLimitOptions = ALLOWED_RECENT_LIMITS;
    const selectValue = recentLimit && ALLOWED_RECENT_LIMITS.includes(recentLimit)
      ? String(recentLimit)
      : '';
    const documentStatusSelectValue = documentStatusFilter
      && ALLOWED_DOCUMENT_STATUS_FILTERS.includes(documentStatusFilter)
      ? documentStatusFilter
      : '';
    const pendingReviewCount = countListingsPendingDocumentReview(
      getAllListings && getAllListings.usersData
    );

    if (loading && !(getAllListings && getAllListings.usersData)) {
      return (
        <div className={cx(s.pagecontentWrapper)}>
          <div className={s.loadingWrap}>
            <Loader type="text" />
          </div>
        </div>
      );
    }

    return (
      <div className={cx(s.pagecontentWrapper)}>
        <div className={s.listingsToolbar}>
          <div className={s.toolbarField}>
            <label htmlFor="recentLimitFilter" className={s.toolbarLabel}>
              {formatMessage(messages.adminListingsRecentLimit)}
            </label>
            <select
              id="recentLimitFilter"
              value={selectValue}
              onChange={this.handleRecentLimitChange}
              className={cx(cs.formControlInput, s.toolbarSelect)}
            >
              <option key="all" value="">
                {formatMessage(messages.adminListingsAll)}
              </option>
              {recentLimitOptions.map((limit) => (
                <option key={limit} value={limit}>
                  {formatMessage(messages.adminListingsNewest, { count: limit })}
                </option>
              ))}
            </select>
          </div>
          <div className={s.toolbarField}>
            <label htmlFor="documentStatusFilter" className={s.toolbarLabel}>
              {formatMessage(messages.adminListingsDocumentStatus)}
            </label>
            <select
              id="documentStatusFilter"
              value={documentStatusSelectValue}
              onChange={this.handleDocumentStatusFilterChange}
              className={cx(cs.formControlInput, s.toolbarSelect)}
            >
              <option key="all-doc-status" value="">
                {formatMessage(messages.adminListingsDocumentStatusAll)}
              </option>
              <option value="pending">
                {formatMessage(messages.carDocumentList_pending)}
              </option>
              <option value="approved">
                {formatMessage(messages.carDocumentList_approved)}
              </option>
              <option value="rejected">
                {formatMessage(messages.carDocumentList_rejected)}
              </option>
            </select>
          </div>
        </div>
        <CommonTable
          thead={this.thead}
          tbody={this.tbody}
          title={formatMessage(messages.listingsManagement)}
          badgeCount={pendingReviewCount > 0 ? pendingReviewCount : undefined}
          className={s.listingsTable}
          isExport
          exportLink={`/export-admin-data?type=listings&search=${searchList}`}
          isSearch
          onSearch={this.handleSearchChange}
        />
        {
          getAllListings?.usersData?.length > 0
          && <div>
            <CustomPagination
              total={getAllListings.count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={pageSize}
              change={this.paginationData}
              paginationLabel={formatMessage(messages.listings)}
            />
          </div>
        }
      </div>
    );
  }

}

const mapState = (state) => ({
});

const mapDispatch = {
  removeListing,
  addListToRecommended,
  removeListFromRecommended,
  ManagePublishStatus,
  
};
export default compose(
  injectIntl,
  withStyles(s, cs),
  connect(mapState, mapDispatch),
  graphql(listingsQuery, {
    name: 'getAllListings',
    options: (props) => ({
      variables: {
        currentPage: props.currentPage,
        searchList: props.searchList,
        recentLimit: props.recentLimit,
        documentStatusFilter: props.documentStatusFilter,
      },
      fetchPolicy: 'network-only',
    })
  })
)(ListingManagement);